import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Search, User, CheckCircle } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import useOnBehalf from '../../hooks/useOnBehalf';

/**
 * Form component for searching and adding existing users
 * Supports both regular user email search and pro club lookup
 */
const AddExistingUserForm = ({ onUserSelected, onCancel }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { searchUserByEmail, searchProUser } = useOnBehalf();
  
  const [emailSearch, setEmailSearch] = useState('');
  const [proLookup, setProLookup] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchType, setSearchType] = useState(null); // 'email' or 'pro'

  const enableProSearch = false;

  const handleEmailSearch = async (e) => {
    e.preventDefault();
    
    if (!emailSearch.trim()) {
      setSearchError('Please enter an email address');
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setSearchResult(null);
    setSearchType('email');
    
    try {
      const user = await searchUserByEmail(emailSearch.trim());
      setSearchResult(
        {...user, id: user._id} // Map _id to id for consistency
      );
    } catch (error) {
      setSearchError(error.message || 'User not found. Please check the email and try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleProSearch = async (e) => {
    e.preventDefault();
    
    if (!proLookup.trim()) {
      setSearchError('Please enter a pro club lookup identifier');
      return;
    }
    
    setIsSearching(true);
    setSearchError(null);
    setSearchResult(null);
    setSearchType('pro');
    
    try {
      const user = await searchProUser(proLookup.trim());
      setSearchResult(
        {...user, id: user._id} // Map _id to id for consistency
      );
    } catch (error) {
      setSearchError(error.message || 'Pro member not found. Please check the identifier and try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddUser = () => {
    if (searchResult) {
      onUserSelected(searchResult);
    }
  };

  const handleClearSearch = useCallback(() => {
    setSearchResult(null);
    setSearchError(null);
    setSearchType(null);
  }, []);

  const labelClassName = clsx(
    'block text-sm font-medium mb-2',
    {
      'text-slate-300': isDark,
      'text-gray-700': !isDark
    }
  );

  const inputClassName = clsx(
    'flex-1 px-3 py-3 border rounded-lg text-sm',
    'focus:outline-none focus:ring-2 focus:ring-green-500',
    {
      'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400': isDark,
      'bg-white border-gray-300 text-gray-900 placeholder-gray-400': !isDark
    }
  );

  const searchButtonClassName = clsx(
    'px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 whitespace-nowrap',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-900 hover:bg-slate-800 text-white focus:ring-slate-700': !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': isDark
    }
  );

  const resultCardClassName = clsx(
    'p-4 rounded-lg border bg-green-50',
    {
      'bg-green-900/20 border-green-700': isDark,
      'bg-green-50 border-green-200': !isDark
    }
  );

  const addButtonClassName = clsx(
    'flex-1 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-900 hover:bg-slate-800 text-white focus:ring-slate-700': 
        searchResult && !isDark,
      'bg-slate-700 hover:bg-slate-600 text-white focus:ring-slate-500': 
        searchResult && isDark,
      'bg-gray-300 text-gray-500 cursor-not-allowed': !searchResult
    }
  );

  const cancelButtonClassName = clsx(
    'px-8 py-3 rounded-lg font-medium transition-all',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    {
      'bg-slate-600 hover:bg-slate-700 text-white focus:ring-slate-500': isDark,
      'bg-gray-200 hover:bg-gray-300 text-gray-700 focus:ring-gray-400': !isDark
    }
  );

  return (
    <div className="space-y-4">
      {/* Email Search Section */}
      <div>
        <label className={labelClassName}>
          User email
        </label>
        <form onSubmit={handleEmailSearch} className="flex gap-2">
          <input
            type="email"
            value={emailSearch}
            onChange={(e) => setEmailSearch(e.target.value)}
            className={inputClassName}
            placeholder="user@example.com"
            disabled={isSearching}
          />
          <button
            type="submit"
            disabled={isSearching || !emailSearch.trim()}
            className={clsx(
              searchButtonClassName,
              (!emailSearch.trim() || isSearching) && 'opacity-50 cursor-not-allowed'
            )}
          >
            {isSearching && searchType === 'email' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Searching...</span>
              </>
            ) : (
              <>
                <Search className="w-4 h-4" />
                <span>Search</span>
              </>
            )}
          </button>
        </form>
      </div>

      {/* Pro Club Lookup Section */}
      {enableProSearch && (
        <div>
          <label className={labelClassName}>
            Pro club lookup
          </label>
          <form onSubmit={handleProSearch} className="flex gap-2">
            <input
              type="text"
              value={proLookup}
              onChange={(e) => setProLookup(e.target.value)}
              className={inputClassName}
              placeholder="Enter pro club identifier"
              disabled={isSearching}
            />
            <button
              type="submit"
              disabled={isSearching || !proLookup.trim()}
              className={clsx(
                searchButtonClassName,
                (!proLookup.trim() || isSearching) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isSearching && searchType === 'pro' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>Search</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Search Error */}
      {searchError && (
        <div className={clsx(
          'p-3 rounded-lg border',
          {
            'bg-red-900/20 border-red-600 text-red-400': isDark,
            'bg-red-50 border-red-200 text-red-600': !isDark
          }
        )}>
          {searchError}
        </div>
      )}

      {/* Search Result */}
      {searchResult && (
        <div className={resultCardClassName}>
          <div className="flex items-start gap-3">
            <div className={clsx(
              'p-2 rounded-full flex-shrink-0',
              {
                'bg-green-800/50': isDark,
                'bg-green-100': !isDark
              }
            )}>
              <CheckCircle className={clsx(
                'w-5 h-5',
                {
                  'text-green-400': isDark,
                  'text-green-600': !isDark
                }
              )} />
            </div>
            
            <div className="flex-1 min-w-0">
              <h4 className={clsx(
                'font-semibold mb-2',
                {
                  'text-slate-200': isDark,
                  'text-gray-900': !isDark
                }
              )}>
                User Found
              </h4>
              
              <div className={clsx(
                'space-y-1 text-sm',
                {
                  'text-slate-300': isDark,
                  'text-gray-700': !isDark
                }
              )}>
                <p>
                  <span className="font-medium">Name:</span> {searchResult.first_name} {searchResult.last_name}
                </p>
                <p>
                  <span className="font-medium">Email:</span> {searchResult.email}
                </p>
                {searchResult.phone_number && (
                  <p>
                    <span className="font-medium">Phone:</span> {searchResult.phone_number}
                  </p>
                )}
                {searchResult.affiliate_id && (
                  <p>
                    <span className="font-medium">Affiliate:</span> {searchResult.affiliate_id}
                  </p>
                )}
              </div>
              
              <button
                onClick={handleClearSearch}
                className={clsx(
                  'mt-3 text-sm underline',
                  {
                    'text-blue-400 hover:text-blue-300': isDark,
                    'text-blue-600 hover:text-blue-700': !isDark
                  }
                )}
              >
                Search for different user
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={handleAddUser}
          disabled={!searchResult}
          className={addButtonClassName}
        >
          <User className="w-4 h-4" />
          <span>Add to Order</span>
        </button>
        
        {onCancel && (
          <button
            onClick={onCancel}
            className={cancelButtonClassName}
          >
            Cancel
          </button>
        )}
      </div>
    </div>
  );
};

AddExistingUserForm.propTypes = {
  /** Callback when user is selected */
  onUserSelected: PropTypes.func.isRequired,
  
  /** Optional cancel callback */
  onCancel: PropTypes.func
};

export default AddExistingUserForm;